import { useRBAC } from '../contexts/RBACContext';

/**
 * Hook for checking permissions in a more declarative way
 */
export const usePermissions = () => {
  const { hasPermission, hasRole, canAccess, hasAnyPermission, hasAllPermissions, rbacData } = useRBAC();

  return {
    // Basic permission checks
    hasPermission,
    hasRole,
    canAccess,
    hasAnyPermission,
    hasAllPermissions,
    
    // Feature-specific permission checks
    canViewDashboard: () => canAccess('dashboard'),
    canViewTenants: () => canAccess('tenants'),
    canCreateTenants: () => canAccess('tenants_create'),
    canEditTenants: () => canAccess('tenants_edit'),
    canDeleteTenants: () => canAccess('tenants_delete'),
    
    canViewUsers: () => canAccess('users'),
    canCreateUsers: () => canAccess('users_create'),
    canEditUsers: () => canAccess('users_edit'),
    canDeleteUsers: () => canAccess('users_delete'),
    
    canViewProducts: () => canAccess('products'),
    canManageProducts: () => canAccess('products_manage'),
    
    canViewIntegrations: () => canAccess('integrations'),
    canManageIntegrations: () => canAccess('integrations_manage'),
    
    canViewRBAC: () => canAccess('rbac'),
    canManageRBAC: () => canAccess('rbac_manage'),
    
    canViewAuditLogs: () => canAccess('audit_logs'),
    
    // Admin checks
    isAdmin: () => rbacData?.summary?.is_admin || false,
    isSuperAdmin: () => hasRole('SUPER_ADMIN') || hasRole('Super Admin'),
    
    // Tenant level checks
    isL0User: () => rbacData?.summary?.access_level?.includes('L0') || false,
    isL2User: () => rbacData?.summary?.access_level?.includes('L2') || false,
    isL3User: () => rbacData?.summary?.access_level?.includes('L3') || false,
    
    // Get user info
    getUserLevel: () => rbacData?.tenant?.level || 'unknown',
    getUserPermissions: () => rbacData?.permission_codes || [],
    getUserRoles: () => rbacData?.role_codes || [],
    
    // Utility functions
    canAccessAdminFeatures: () => hasAnyPermission([
      'ADM_VIEW_DASHBOARD',
      'ADM_VIEW_TENANTS', 
      'ADM_VIEW_USERS',
      'ADM_VIEW_PRODUCTS',
      'ADM_VIEW_INTEGRATIONS'
    ]),
    
    canManageAnyResource: () => hasAnyPermission([
      'ADM_CREATE_TENANTS',
      'ADM_EDIT_TENANTS',
      'ADM_DELETE_TENANTS',
      'ADM_CREATE_USERS',
      'ADM_EDIT_USERS',
      'ADM_DELETE_USERS',
      'ADM_MANAGE_PRODUCTS',
      'ADM_MANAGE_INTEGRATIONS',
      'ADM_MANAGE_ACCESS_CONTROL'
    ])
  };
};

export default usePermissions;